// src/api/chatSocket.js
import axios from 'axios';
import { io } from 'socket.io-client';
import './axiosInstance'; // side-effect: sets axios.defaults.baseURL

const EVENTS = ['chat:message', 'chat:typing', 'chat:read', 'chat:presence', 'chat:unread', 'chat:reaction'];

let socket = null;
let activeJobId = null;
let boundToken = null;
const listeners = new Map(); // event -> Set<fn>

const currentToken = () =>
  typeof window !== 'undefined' ? window.localStorage.getItem('accessToken') : null;

const origin = () => axios.defaults.baseURL || 'http://localhost:5002';

const emitLocal = (event, payload) => {
  const set = listeners.get(event);
  if (!set) return;
  set.forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      /* a bad subscriber must not break the transport */
    }
  });
};

const bind = (s) => {
  EVENTS.forEach((ev) => s.on(ev, (payload) => emitLocal(ev, payload)));
  s.on('connect', () => {
    if (activeJobId) s.emit('chat:subscribe', { jobId: activeJobId });
    emitLocal('connect');
  });
  s.on('disconnect', (reason) => emitLocal('disconnect', reason));
  s.on('connect_error', (err) => emitLocal('connect_error', err));
};

export const chatSocket = {
  connect() {
    const token = currentToken();
    if (!token) return null;
    if (socket) {
      // Token rotated (e.g. re-login without a full reload) -> rebuild.
      if (token !== boundToken) {
        chatSocket.disconnect();
      } else {
        return socket;
      }
    }
    boundToken = token;
    socket = io(origin(), {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
    });
    bind(socket);
    return socket;
  },

  disconnect() {
    if (!socket) return;
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    activeJobId = null;
    boundToken = null;
  },

  isConnected: () => Boolean(socket && socket.connected),

  on(event, fn) {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event).add(fn);
    return () => chatSocket.off(event, fn);
  },

  off(event, fn) {
    const set = listeners.get(event);
    if (set) set.delete(fn);
  },

  subscribe(jobId) {
    activeJobId = jobId || null;
    if (socket && jobId) socket.emit('chat:subscribe', { jobId });
  },

  send(payload, ack) {
    if (socket) socket.emit('chat:send', payload, ack);
  },

  typing(jobId, isTyping) {
    if (socket && jobId) socket.emit('chat:typing', { jobId, isTyping: Boolean(isTyping) });
  },
};

export default chatSocket;
